import React, { useEffect, useState } from 'react';
import menu from '../data/menu.json';
import clsx from 'clsx';
import { useLocation, useNavigate } from 'react-router-dom';
import { Bars3Icon, ChevronDownIcon, UserIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Accordion } from '../components/base/Accordion';
import uiConfig from '../data/uiConfig';
import { Button } from '../components/base/Button';
import { ButtonVariant } from '../utils/utils';
import ShoppingButton from '../components/base/ShoppingButton';
import { useUser } from "../context/user-context";

interface MenuMobileProps {
  btnContact?: boolean;
}

export const MenuMobile: React.FC<MenuMobileProps> = ({
  btnContact = false
}) => {
  const [open, setOpen] = useState(false);
  const [visible, setVisible] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const txtColor = uiConfig.navbar.color;
  const bgColor = uiConfig.navbar.bgColor;
  const hoverColor = uiConfig.navbar.hover;
  const activeClass = uiConfig.navbar.active;
  const iconColor = uiConfig.navbar.icon;
  const navbarBtn = uiConfig.navbar.btnClass;
  const navbarType = uiConfig.navbar.btnType as ButtonVariant;

  const { user } = useUser();
  const isAdmin = !!user && user.role === 'admin';
  const connected = !!user;

  function handleItemClick(item: any) {
    if (!item.subMenu) {
      setOpen(false);
      if (item.path) navigate(item.path);
    }
  }

  useEffect(() => {
    if (open) {
      setVisible(true);
    } else {
      // Attend la fin de l'animation avant de cacher
      const timeout = setTimeout(() => setVisible(false), 380); // 380ms = durée de l'animation (moins sinon le menu réapparait rapidemnent)
      return () => clearTimeout(timeout);
    }
  }, [open]);

  return (
    <>
      {/* Hamburger pour mobile */}
      {connected ? (
        <div className='md:hidden flex gap-x-4 items-center'>
          <ShoppingButton />
          <button
            className={`rounded-md border border-default p-2 z-40 ${bgColor}`}
            onClick={() => setOpen(!open)}
            aria-label="Ouvrir le menu"
          >
            {open ?
              <XMarkIcon className={`h-6 w-6 ${txtColor}`} />
              :
              <Bars3Icon className={`h-6 w-6 ${txtColor}`} />
            }
          </button>
        </div>
      ) : (
        <div className='md:hidden flex gap-x-4 items-center'>
          <Button onClick={() => navigate('/connexion?view=login')} className='md:ml-auto'>Connexion</Button>
        </div>
      )}

      {/* Menu mobile */}
      {visible && (
        <>
          {/* Overlay qui ferme le menu au clic */}
          <div
            className={
              `fixed inset-0 top-menu-overlay z-30 md:hidden ${open ? 'block' : 'hidden'}`}
            onClick={() => setOpen(false)}
          />

          <div
            className={clsx(
              "w-full transition-all md:hidden border-t border-default mt-3 z-40 overflow-hidden",
              bgColor,
              txtColor,
              open ? 'animate-menu-top-open' : 'animate-menu-top-close'
            )}>
            <div className='h-full'>
              <div className="flex flex-col w-full py-1" aria-label="Menu mobile principal">
                {Object.values(menu).map((item: any, idx) => (
                  ((item.isAdmin && isAdmin) || item.isAdmin === undefined) && (
                    <div key={idx}>
                      {item.subMenu ? (
                        <Accordion
                          title={
                            <span className={clsx(
                              txtColor,
                              (location.pathname === item.path ||
                                (item.path === "/backoffice" && location.pathname.includes(item.path)) ||
                                item.subMenu?.some((child: any) => child.path === location.pathname)) &&
                              activeClass
                            )}>
                              {item.name}
                            </span>
                          }
                        >
                          <div className="flex flex-col">
                            {item.subMenu.map((child: any, cidx: number) => (
                              <a
                                key={cidx}
                                href={child.path}
                                className={clsx(
                                  "block px-4 py-2",
                                  txtColor,
                                  hoverColor,
                                  location.pathname === child.path && activeClass
                                )}
                                onClick={() => setOpen(false)}
                              >
                                {child.name}
                              </a>
                            ))}
                          </div>
                        </Accordion>
                      ) : (
                        <button
                          type="button"
                          className={clsx(
                            "flex w-full items-center p-2",
                            txtColor,
                            hoverColor,
                            (location.pathname === item.path ||
                              (item.path === "/backoffice" && location.pathname.includes(item.path))) &&
                            activeClass
                          )}
                          onClick={() => handleItemClick(item)}
                        >
                          {item.name}
                        </button>
                      )}
                    </div>
                  ))
                )}
                {connected && (
                  <a href="/utilisateur" className={clsx(
                    "flex w-full items-center p-2",
                    txtColor,
                    hoverColor,
                    (location.pathname === "/utilisateur" || location.pathname.includes("/utilisateur")) &&
                    activeClass
                  )}>
                    Mon compte
                  </a>
                )}
              </div>

              {btnContact &&
                <div className="flex flex-col items-center ml-auto border-t border-default pt-4">
                  <Button
                    className={navbarBtn}
                    onClick={() => {
                      setOpen(false);
                      navigate('/contact')
                    }}
                    variant={navbarType}
                  >
                    Contact
                  </Button>
                </div>

              }
            </div>
          </div>
        </>
      )}
    </>
  );
};